import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { CourseSelector } from "./CourseSelector";
import { PackageSelector } from "./PackageSelector";
import { UserSelector } from "./UserSelector";
import { useToast } from "../ui/ToastContainer";
import { enrollmentService } from "../../services/enrollmentService";
import { packageService } from "../../services/packageService";
import {
  parseApiError,
  formatValidationErrors,
} from "../../utils/errorHandler";

interface AddEnrollmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddEnrollmentDialog({
  isOpen,
  onClose,
  onSuccess,
}: AddEnrollmentDialogProps) {
  const [formData, setFormData] = useState({
    userId: "",
    couponCode: "",
    isFree: false,
    enrollmentExpiresAt: "",
  });
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  // Bumped each time the dialog opens; used as a React `key` on the two
  // selectors to remount them and clear their internal `selectedXxxMap` state.
  const [resetKey, setResetKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const { showSuccess, showError } = useToast();

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setFormData({
        userId: "",
        couponCode: "",
        isFree: false,
        enrollmentExpiresAt: "",
      });
      setSelectedCourseIds([]);
      setSelectedPackageIds([]);
      setError(null);
      setValidationErrors({});
      // Remount selectors so their internal `selectedXxxMap` is empty.
      setResetKey((k) => k + 1);
    }
  }, [isOpen]);

  // Mutual exclusion: selecting any package clears courses, and vice versa
  const handleCoursesChange = (ids: string[]) => {
    setSelectedCourseIds(ids);
    if (ids.length > 0 && selectedPackageIds.length > 0) {
      setSelectedPackageIds([]);
    }
  };

  const handlePackagesChange = (ids: string[]) => {
    setSelectedPackageIds(ids);
    if (ids.length > 0 && selectedCourseIds.length > 0) {
      setSelectedCourseIds([]);
    }
    // When packages are selected, drop any stale expiration date so the
    // package's validityPeriod is used. The date input is disabled while
    // packages are selected, but its value in state would otherwise be
    // silently overridden by the backend's manualExpiresAt branch.
    if (ids.length > 0 && formData.enrollmentExpiresAt !== "") {
      setFormData({ ...formData, enrollmentExpiresAt: "" });
    }
  };

  // Convenience booleans used by submit logic and the submit-button label
  const hasPackages = selectedPackageIds.length > 0;
  const hasCourses = selectedCourseIds.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!formData.userId || (!hasCourses && !hasPackages)) {
        throw new Error(
          "Please select a student and at least one course or package.",
        );
      }

      if (hasPackages) {
        // Packages branch — admin grants the package(s) to the student
        await Promise.all(
          selectedPackageIds.map((packageId) =>
            packageService.grantPackageToUser(
              formData.userId,
              packageId,
              formData.enrollmentExpiresAt || undefined,
              formData.couponCode || undefined,
            ),
          ),
        );

        showSuccess(
          `Successfully granted ${selectedPackageIds.length} package${
            selectedPackageIds.length > 1 ? "s" : ""
          }`,
          "Success",
        );
      } else {
        // Courses branch — existing flow, unchanged
        await Promise.all(
          selectedCourseIds.map((courseId) =>
            enrollmentService.createEnrollment({
              user: formData.userId,
              course: courseId,
              isFree: formData.isFree,
              couponCode: formData.couponCode || undefined,
              enrollmentExpiresAt: formData.enrollmentExpiresAt || undefined,
            }),
          ),
        );

        showSuccess(
          `Successfully created ${selectedCourseIds.length} enrollment${
            selectedCourseIds.length > 1 ? "s" : ""
          }`,
          "Success",
        );
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Enrollment creation failed:", err);
      const apiError = parseApiError(err);

      // Set validation errors if present
      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      // Show toast notification
      showError(apiError.message, "Enrollment Failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Enrollment">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            <p className="font-medium">{error}</p>
            {Object.keys(validationErrors).length > 0 && (
              <ul className="mt-2 ml-4 list-disc space-y-1">
                {formatValidationErrors(validationErrors).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{err.field}:</span>{" "}
                    {err.messages.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Student Select */}
        <UserSelector
          value={formData.userId}
          onChange={(val) => setFormData({ ...formData, userId: val })}
          role="student"
          placeholder="Search by student name or email..."
        />

        {/* Course Select (Multi) — disabled when packages are selected; remounted on dialog open to clear internal map */}
        <CourseSelector
          key={`courses-${resetKey}`}
          selectedIds={selectedCourseIds}
          onChange={handleCoursesChange}
          placeholder="Search and select courses..."
          disabled={hasPackages}
        />

        {/* Package Select (Multi) — mutually exclusive with courses; remounted on dialog open to clear internal map */}
        <PackageSelector
          key={`packages-${resetKey}`}
          selectedIds={selectedPackageIds}
          onChange={handlePackagesChange}
          placeholder="Search and select packages..."
          disabled={hasCourses}
        />
        {hasCourses && (
          <p className="text-[10px] text-amber-600 -mt-1">
            Clear selected courses to enable packages.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Expiration Date */}
          <div className="space-y-2">
            <label
              htmlFor="enrollmentExpiresAt"
              className="text-sm font-medium text-gray-700"
            >
              Expiration Date (Optional)
            </label>
            <Input
              id="enrollmentExpiresAt"
              type="date"
              value={formData.enrollmentExpiresAt}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  enrollmentExpiresAt: e.target.value,
                })
              }
              disabled={hasPackages}
            />
            <p className="text-[10px] text-gray-500">
              Leave empty to use course duration or 1-year default.
            </p>
            {hasPackages && (
              <p role="status" aria-live="polite" className="text-[10px] text-gray-500">
                Packages use their own validity period.
              </p>
            )}
          </div>

          {/* Coupon Code */}
          <div className="space-y-2">
            <label
              htmlFor="couponCode"
              className="text-sm font-medium text-gray-700"
            >
              Coupon Code (Optional)
            </label>
            <Input
              id="couponCode"
              placeholder="Enter coupon code"
              value={formData.couponCode}
              onChange={(e) =>
                setFormData({ ...formData, couponCode: e.target.value })
              }
              disabled={formData.isFree}
            />
            {hasPackages && (
              <p
                role="status"
                aria-live="polite"
                className="text-[10px] text-gray-500"
              >
                Enter a package coupon code to discount this grant.
              </p>
            )}
          </div>
        </div>

        {/* Is Free Toggle */}
        <div className="flex items-center gap-2 pt-2">
          <input
            type="checkbox"
            id="isFree"
            checked={formData.isFree}
            onChange={(e) =>
              setFormData({ ...formData, isFree: e.target.checked })
            }
            disabled={hasPackages}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <label htmlFor="isFree" className="text-sm font-medium text-gray-700">
            Override as Free Enrollment?
          </label>
          {hasPackages && (
            <span
              role="status"
              aria-live="polite"
              className="text-[10px] text-gray-500 ml-2"
            >
              Packages have their own pricing.
            </span>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            className="w-32"
          >
            {isLoading ? "Creating..." : hasPackages ? "Grant Packages" : "Create Enroll"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
